import { Box } from "@chakra-ui/react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { PlaybackButton } from "@/components";
import { KNIT_SHORT_ROWS } from "@/content";
import { usePlaybackToggle } from "@/hooks";
import {
  COURSE_HEIGHT,
  LOOP_WIDTH,
  SWATCH_NEEDLES,
  SWATCH_ROWS,
  SWATCH_STEP_ROWS,
  getSwatch,
  heldBefore,
  rowDirection,
  type Swatch,
} from "./shortRows";

const ROWS = SWATCH_ROWS.length;

const ROW_SECONDS = 0.16;

const HOLD_SECONDS = 2.5;

const LOOP_SECONDS = ROWS * ROW_SECONDS + HOLD_SECONDS;

// Where each step shows best: the full rows done, the first short row on its way to its turn, the
// first wedge done, and the whole swatch.
const STEP_SECONDS = [
  4 * ROW_SECONDS - 0.01,
  4.7 * ROW_SECONDS,
  SWATCH_STEP_ROWS[3] * ROW_SECONDS - 0.01,
  ROWS * ROW_SECONDS + 0.5,
];

// Without motion, the whole swatch.
const STILL_SECONDS = STEP_SECONDS[3];

const COLOURS = {
  grid: "#e3e0dc",
  knitted: "#6b665f",
  held: "#cccac8",
  course: "#f75f2d",
  yarn: "#ebe7e1",
  heldYarn: "#b3ada5",
  outline: "#474746",
};

// In loop widths: the yarn, its outline, and how far a loop's head and sinker loops sit from its
// place in the relaxed swatch.
const YARN_WIDTH = 0.2;
const OUTLINE_WIDTH = 0.29;
const HEAD_ABOVE = 0.62 * COURSE_HEIGHT;
const SINKER_BELOW = 1.3 * COURSE_HEIGHT;
const BULGE = 0.16;

const MAX_PIXEL_RATIO = 2;

type Vec = { readonly x: number; readonly y: number };
type YarnPoint = Vec & { readonly front: boolean };

const add = (a: Vec, b: Vec, scale = 1): Vec => ({ x: a.x + b.x * scale, y: a.y + b.y * scale });
const unit = (v: Vec): Vec => {
  const length = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / length, y: v.y / length };
};
const dot = (a: Vec, b: Vec) => a.x * b.x + a.y * b.y;

const stateAt = (seconds: number) => {
  const rows = Math.min(seconds / ROW_SECONDS, ROWS);
  const row = Math.min(Math.floor(rows), ROWS - 1);
  return { row, progress: rows >= ROWS ? 1 : rows - row };
};

const stepAt = (row: number) =>
  SWATCH_STEP_ROWS.reduce<number>((current, start, index) => (row >= start ? index : current), 0);

// Each loop's place and its directions: along its row, toward higher needles, and up its wale.
const frames = (swatch: Swatch) => {
  const { loops, rows, at } = swatch;
  const place = (loop: number): Vec => ({ x: at[loop * 2], y: at[loop * 2 + 1] });
  const byNeedle = rows.map((row) => {
    const needles = new Map<number, number>();
    row.forEach((loop) => needles.set(loops[loop].needle, loop));
    return needles;
  });
  return loops.map((loop, index) => {
    const row = byNeedle[loop.row];
    const before = row.get(loop.needle - 1);
    const after = row.get(loop.needle + 1);
    const along = unit({
      x:
        (after !== undefined ? at[after * 2] : at[index * 2]) - (before !== undefined ? at[before * 2] : at[index * 2]),
      y:
        (after !== undefined ? at[after * 2 + 1] : at[index * 2 + 1]) -
        (before !== undefined ? at[before * 2 + 1] : at[index * 2 + 1]),
    });
    return { place: place(index), along, up: { x: -along.y, y: along.x } };
  });
};

type Frame = ReturnType<typeof frames>[number];

// One loop between its sinker loops, its head above its place: legs in front, head and sinker loops
// behind, as plain jersey's face shows it.
const loopPoints = (frame: Frame, sinkLeft: Vec, sinkRight: Vec): YarnPoint[] => {
  const head = add(frame.place, frame.up, HEAD_ABOVE);
  const middle = { x: (sinkLeft.x + sinkRight.x) / 2, y: (sinkLeft.y + sinkRight.y) / 2 };
  const rise = { x: head.x - middle.x, y: head.y - middle.y };
  return Array.from({ length: 25 }, (_, step) => {
    const t = -Math.PI + (2 * Math.PI * step) / 24;
    const s = (t + Math.PI) / (2 * Math.PI);
    const base = { x: sinkLeft.x + (sinkRight.x - sinkLeft.x) * s, y: sinkLeft.y + (sinkRight.y - sinkLeft.y) * s };
    const lift = (1 + Math.cos(t)) / 2;
    const up = dot({ x: head.x - base.x, y: head.y - base.y }, frame.up) * lift;
    const across = dot(rise, frame.along) * lift + BULGE * LOOP_WIDTH * Math.sin(2 * t);
    return {
      x: base.x + frame.up.x * up + frame.along.x * across,
      y: base.y + frame.up.y * up + frame.along.y * across,
      front: Math.cos(2 * t) < 0,
    };
  });
};

type Strand = { readonly points: YarnPoint[]; readonly colour: string };

// The yarn up to where the carriage has got: each row's loops in the order it knitted them, joined
// at each end by the yarn turning into the next row.
const strands = (swatch: Swatch, row: number, progress: number): Strand[] => {
  const { loops, rows } = swatch;
  const loopFrames = frames(swatch);
  const held = new Set(heldBefore(swatch, row));
  const [first, last] = SWATCH_ROWS[row];
  const isHeld = (loop: number) => held.has(loop) && (loops[loop].needle < first || loops[loop].needle > last);
  const sinkerUnder = (a: number, b: number): Vec => {
    const up = unit(add(loopFrames[a].up, loopFrames[b].up));
    const middle = {
      x: (loopFrames[a].place.x + loopFrames[b].place.x) / 2,
      y: (loopFrames[a].place.y + loopFrames[b].place.y) / 2,
    };
    return add(middle, up, -SINKER_BELOW);
  };
  const edgeSinker = (loop: number, side: 1 | -1): Vec =>
    add(
      add(loopFrames[loop].place, loopFrames[loop].along, (side * LOOP_WIDTH) / 2),
      loopFrames[loop].up,
      -SINKER_BELOW,
    );

  const result: Strand[] = [];
  let previousEnd: Vec | null = null;
  for (let current = 0; current <= row; current += 1) {
    const order = rows[current];
    const count = current < row ? order.length : Math.ceil(progress * order.length);
    const direction = rowDirection(current);
    const rowColour = current === row ? COLOURS.course : COLOURS.yarn;
    const start = edgeSinker(order[0], -direction as 1 | -1);
    if (previousEnd && count > 0) {
      // the turn: out past the end loop and back in, a row up
      const outward = add({ x: 0, y: 0 }, loopFrames[order[0]].along, -direction);
      const bend = add({ x: (previousEnd.x + start.x) / 2, y: (previousEnd.y + start.y) / 2 }, outward, 0.32);
      const turn = Array.from({ length: 9 }, (_, step) => {
        const t = step / 8;
        const a = add(previousEnd!, { x: bend.x - previousEnd!.x, y: bend.y - previousEnd!.y }, t);
        const b = add(bend, { x: start.x - bend.x, y: start.y - bend.y }, t);
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, front: false };
      });
      result.push({ points: turn, colour: rowColour });
    }
    let sinker = start;
    for (let index = 0; index < count; index += 1) {
      const loop = order[index];
      const next = order[index + 1];
      const end = next !== undefined ? sinkerUnder(loop, next) : edgeSinker(loop, direction);
      const [left, right] = direction === 1 ? [sinker, end] : [end, sinker];
      const points = loopPoints(loopFrames[loop], left, right);
      if (direction === -1) points.reverse();
      const colour = current === row ? COLOURS.course : isHeld(loop) ? COLOURS.heldYarn : COLOURS.yarn;
      result.push({ points, colour });
      sinker = end;
    }
    previousEnd = sinker;
    if (count < order.length) break;
  }
  return result;
};

const drawFabric = (
  context: CanvasRenderingContext2D,
  swatch: Swatch,
  row: number,
  progress: number,
  box: { x: number; y: number; width: number; height: number },
) => {
  const { bounds } = swatch;
  const margin = 1.2;
  const scale = Math.min(
    box.width / (bounds.right - bounds.left + 2 * margin),
    box.height / (bounds.top - bounds.bottom + 2 * margin),
  );
  const originX = box.x + box.width / 2 - ((bounds.left + bounds.right) / 2) * scale;
  const originY = box.y + box.height / 2 + ((bounds.bottom + bounds.top) / 2) * scale;
  const toCanvas = (point: Vec) => ({ x: originX + point.x * scale, y: originY - point.y * scale });

  const runs: { points: Vec[]; colour: string; front: boolean }[] = [];
  for (const strand of strands(swatch, row, progress)) {
    let start = 0;
    for (let index = 1; index <= strand.points.length; index += 1) {
      if (index < strand.points.length && strand.points[index].front === strand.points[start].front) continue;
      runs.push({
        points: strand.points.slice(start, Math.min(index + 1, strand.points.length)).map(toCanvas),
        colour: strand.colour,
        front: strand.points[start].front,
      });
      start = index;
    }
  }
  context.lineJoin = "round";
  const stroke = (run: (typeof runs)[number]) => {
    context.beginPath();
    run.points.forEach((point, index) => (index ? context.lineTo(point.x, point.y) : context.moveTo(point.x, point.y)));
    context.lineCap = "butt";
    context.strokeStyle = COLOURS.outline;
    context.lineWidth = OUTLINE_WIDTH * scale;
    context.stroke();
    context.lineCap = "round";
    context.strokeStyle = run.colour;
    context.lineWidth = YARN_WIDTH * scale;
    context.stroke();
  };
  // the head and sinker loops behind, then the legs over them
  runs.filter((run) => !run.front).forEach(stroke);
  runs.filter((run) => run.front).forEach(stroke);
};

// The knitmap: a cell a needle in each row, the first row at the bottom. Knitted cells dark, the
// row on the needles orange as the carriage goes, and grey where a needle holds its loop.
const drawKnitmap = (
  context: CanvasRenderingContext2D,
  row: number,
  progress: number,
  box: { x: number; y: number; cell: number },
) => {
  const { x, y, cell } = box;
  const gap = Math.max(1, cell * 0.12);
  context.strokeStyle = COLOURS.grid;
  context.lineWidth = Math.max(1, cell * 0.06);
  context.strokeRect(x, y, SWATCH_NEEDLES * cell, ROWS * cell);
  for (let current = 0; current <= row; current += 1) {
    const [first, last] = SWATCH_ROWS[current];
    const count = last - first + 1;
    const reached = current < row ? count : Math.ceil(progress * count);
    const top = y + (ROWS - 1 - current) * cell;
    for (let needle = 0; needle < SWATCH_NEEDLES; needle += 1) {
      const inRow = needle >= first && needle <= last;
      const order = rowDirection(current) === 1 ? needle - first : last - needle;
      if (inRow && order >= reached) continue;
      context.fillStyle = !inRow ? COLOURS.held : current === row ? COLOURS.course : COLOURS.knitted;
      context.fillRect(x + needle * cell + gap / 2, top + gap / 2, cell - gap, cell - gap);
    }
  }
};

export type ShortRowSwatchHandle = {
  readonly showStep: (step: number) => void;
};

type ShortRowSwatchProps = {
  readonly onStepChange: (step: number) => void;
};

// A swatch with two wedges of short rows, knitted row by row: its knitmap on the left, and on the
// right the same loops as the fabric lies off the needles.
export const ShortRowSwatch = forwardRef<ShortRowSwatchHandle, ShortRowSwatchProps>(({ onStepChange }, ref) => {
  const { isPlaying, toggle } = usePlaybackToggle();
  const [isOnScreen, setIsOnScreen] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const secondsRef = useRef(STILL_SECONDS);
  const redrawRef = useRef(true);
  const stepRef = useRef(-1);
  const onStepChangeRef = useRef(onStepChange);

  useEffect(() => {
    onStepChangeRef.current = onStepChange;
  });

  const showStep = useCallback((step: number) => {
    secondsRef.current = STEP_SECONDS[step];
    redrawRef.current = true;
  }, []);

  useImperativeHandle(ref, () => ({ showStep }), [showStep]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new IntersectionObserver(([entry]) => setIsOnScreen(entry.isIntersecting));
    observer.observe(canvas);
    const handleVisibility = () => setIsPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    redrawRef.current = true;
  }, [isPlaying]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context || !isOnScreen || !isPageVisible) return;
    const swatch = getSwatch();
    const playing = isPlaying;
    let last = performance.now();

    let frame = requestAnimationFrame(function tick(now) {
      const elapsed = Math.min((now - last) / 1000, 0.1);
      last = now;
      if (playing) {
        secondsRef.current = (secondsRef.current + elapsed) % LOOP_SECONDS;
        redrawRef.current = true;
      }
      const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      const width = Math.max(1, Math.round(canvas.clientWidth * ratio));
      const height = Math.max(1, Math.round(canvas.clientHeight * ratio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        redrawRef.current = true;
      }
      if (redrawRef.current) {
        redrawRef.current = false;
        const { row, progress } = stateAt(secondsRef.current);
        context.clearRect(0, 0, width, height);
        const pad = height * 0.07;
        const cell = (height - 2 * pad) / ROWS;
        drawKnitmap(context, row, progress, { x: pad, y: pad, cell });
        const fabricLeft = pad + SWATCH_NEEDLES * cell + pad;
        drawFabric(context, swatch, row, progress, {
          x: fabricLeft,
          y: 0,
          width: width - fabricLeft,
          height,
        });
        const step = stepAt(row);
        if (step !== stepRef.current) {
          stepRef.current = step;
          onStepChangeRef.current(step);
        }
      }
      frame = requestAnimationFrame(tick);
    });

    return () => cancelAnimationFrame(frame);
  }, [isPlaying, isOnScreen, isPageVisible]);

  return (
    <Box
      as="figure"
      className="print-avoid-break"
      borderRadius="lg"
      overflow="hidden"
      bg="surface.white"
      border="1px solid"
      borderColor="line.hairline"
    >
      <Box position="relative" sx={{ aspectRatio: "7 / 5" }}>
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={KNIT_SHORT_ROWS.description}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        />
        <PlaybackButton isPlaying={isPlaying} onToggle={toggle} subject="animation" />
      </Box>
    </Box>
  );
});

ShortRowSwatch.displayName = "ShortRowSwatch";
